import type { RefObject } from 'react';
import { useEffect } from 'react';

import type { QueryStatus } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import aiGeneratedWatermark from '#src/app/pages/petitionDetails/shared/assets/ai-generated-watermark.svg';

import type { WatermarkPlacement } from './utils';
import { compositeWatermark } from './utils';

type Params = Readonly<{
	src: string;
	placement: WatermarkPlacement;
	/**
	 * Element whose layout width is the image's rendered width — measured at composite time to
	 * convert the placement's CSS-px padding into image px. A CSS-hidden element measures 0 and
	 * is treated as unmeasurable (padding then bakes as raw px against the file).
	 */
	measureRef: RefObject<HTMLElement | null>;
	onCompositeError?: () => void;
}>;

type Result = ModelHookResult<{
	status: QueryStatus;
	watermarkedUrl: string | undefined;
}>;

const QUERY_KEY = 'petition-details-watermarked-image';

export function useWatermarkedImage({ src, placement, measureRef, onCompositeError }: Params): Result {
	/* eslint-disable @tanstack/query/exhaustive-deps -- the rendered width (measureRef) is deliberately read once at composite time
	a resize must not re-run the composite (accepted drift), and a layout read can't be part of a render-time key */
	const {
		data: watermarkedUrl,
		status,
		errorUpdatedAt,
	} = useQuery({
		queryKey: [QUERY_KEY, src, placement],
		queryFn: async ({ signal }) => {
			const renderedWidth = measureRef.current?.clientWidth || undefined;
			const blob = await compositeWatermark(src, aiGeneratedWatermark, placement, renderedWidth);
			// src may have moved on to a different query while we were compositing — bail out
			// instead of creating an object url that will never have a chance to be revoked
			if (signal.aborted) throw signal.reason;
			return URL.createObjectURL(blob);
		},
		// compositing failures are deterministic (broken image, tainted canvas) — retrying would
		// only delay the fallback to the unmarked original while the hero stays withheld
		retry: false,
		// intentionally uncached (matches the app-wide default, but stated here because it's
		// load-bearing): the unmount cleanup revokes the object url, so a cached entry could
		// hand a revoked url to a later consumer. Revisit only if compositing shows up in perf.
		gcTime: 0,
		staleTime: 0,
	});
	/* eslint-enable @tanstack/query/exhaustive-deps */

	// react-query owns the fetch/error state; this effect only owns revoking the object url it produced
	useEffect(() => {
		return () => {
			if (watermarkedUrl) URL.revokeObjectURL(watermarkedUrl);
		};
	}, [watermarkedUrl]);

	useEffect(() => {
		if (status === 'error') onCompositeError?.();
	}, [status, errorUpdatedAt]);

	return {
		data: {
			status,
			watermarkedUrl,
		},
	};
}
